import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiAccountService } from '@services/api-account.service';
import { ApiCustomerService } from '@services/api-customer.service';
import { SharedService } from '@services/shared.service';
import { FormBase } from '@shared/form-base.component';
import { CustomerCardModel } from '@shared/models/CardModel';
import { CustomerModel } from '@shared/models/CustomerModel';
import { Country } from '@shared/models/Enums';
import { EventModel } from '@shared/models/EventModel';
import { CardPaymentModel } from '@shared/models/RegisterModel';
import { CardFormComponent } from '@components/card-form/card-form.component';
import { SweetAlertResult } from 'sweetalert2';

@Component({
    selector: 'app-payment-method',
    templateUrl: './payment-method.component.html',
    styleUrls: ['./payment-method.component.scss']
})
export class PaymentMethodComponent extends FormBase<any> implements OnInit {

    @Input() customer: CustomerModel;
    @Input() guestPurchase = false;
    @Input() fullForm = false;
    @Input() total: number;
    @Input() checkoutType: string;
    @Input() event: EventModel = null;// to show id number input for puerto capital events

    @Output() onSetCurrentMethod: EventEmitter<CustomerCardModel> = new EventEmitter<CustomerCardModel>();
    @Output() onSetGuestPurchase: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();
    @Output() onSetCardForm: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();

    guestForm: UntypedFormGroup;

    paymentMethods: CustomerCardModel[] = [];
    currentMethod: CustomerCardModel;
    cardGuestPayment: CardPaymentModel;
    selectedMethod = "";

    newCard: CardPaymentModel;


    notRegisteredEmail = true;

    get showIdNumber(): boolean {

        let show = false;
        if (this.checkoutType == 'ev') {
            // mostrar input de cédula en caso de puerto capital 
            // && this.event.vendor.id == 83
            if (this.event.country == Country.CR) {
                show = true;
            }
        }

        return show;
    }


    constructor(public route: ActivatedRoute,
        public router: Router,
        public sharedService: SharedService,
        public modalService: NgbModal,
        private formBuilder: UntypedFormBuilder,
        private apiCustomerService: ApiCustomerService,
        private apiAccountService: ApiAccountService,) {
        super();
    }

    ngOnInit(): void {

        if (this.fullForm && this.guestPurchase) this._formGuestInit();

        if (!this.guestPurchase) this._getPaymentMethods();
    }

    onSelectMethod(card: CustomerCardModel) {

        this.currentMethod = card;
        this.onSetCurrentMethod.emit(this.currentMethod);
    }

    onCreateCard(paymentMethodModal: any) {

        this.newCard = null;

        this.modalService.open(paymentMethodModal, {
            backdrop: "static",
            keyboard: false,
            windowClass: "modal-dialog-end modal-full",
        });
    }

    @ViewChild(CardFormComponent) cardFormComponent: CardFormComponent;

    // País del checkout: define la pasarela con la que se agrega la tarjeta
    @Input() country: Country = Country.CR;

    // La tarjeta la arma app-card-form y llega por (cardChange). null = incompleta.
    onCardChange(card: CardPaymentModel) {

        this.newCard = card;
    }

    /**
     * El formulario de tarjeta de la compra sin registro. Se sigue emitiendo el
     * FormGroup hacia arriba porque es lo que los checkouts leen hoy; lo que cambia
     * es que ahora ese form lo arma app-card-form, y con Stripe trae `token` y los
     * últimos 4 dígitos en vez de número, expiración y CVC.
     */
    onGuestCardChange(card: CardPaymentModel) {

        if (card == null) return;

        this.onSetCardForm.emit(this.cardFormComponent?.cardForm);
    }

    onSaveCard(modal: any): void {

        if (!this.newCard) return;

        this.newCard.companyId = this.customer.id;

        if (this.cardFormComponent?.isAmexCVC) {
            const errMsg =
                "No se aceptan tarjetas American Express. Por favor revisá la información y volvé a intentarlo.";
            this.sharedService.showErrorAlert(null, errMsg);
        } else {
            this.sharedService.setLoading(true);

            this.apiAccountService.addCard(this.newCard).then(
                (data: any) => {
                    this.sharedService.setLoading(false); // hide loader

                    this._getPaymentMethods();

                    this.sharedService
                        .showSuccessAlert(
                            null,
                            `La tarjeta fue agregada correctamente`
                        )
                        .then((_) => modal.dismiss());
                },
                (err: any) => {
                    this.sharedService.setLoading(false); // hide loader

                    const errMsg =
                        "No se pudo agregar la tarjeta. Por favor revisá la información y volvé a intentarlo.";
                    this.sharedService.showErrorAlert(null, errMsg);
                }
            );
        }
    }

    onDeleteMethod(card: CustomerCardModel) {

        this.sharedService.showConfirmAlert(
            null,
            `¿Deseás eliminar la tarjeta terminada en "${card.masked}"?`
        ).then(
            (result: SweetAlertResult) => {

                if (result && result.value) {

                    this.sharedService.setLoading(true);

                    this.apiAccountService.deleteCard(card.token).then(
                        (data: any) => {

                            this._getPaymentMethods();

                            this.sharedService.setLoading(false); // hide loader

                            this.sharedService.showSuccessAlert(
                                null, `La tarjeta fue eliminada correctamente`
                            );
                        },
                        (err: any) => {
                            this.sharedService.setLoading(false); // hide loader

                            const errMsg = 'No se pudo eliminar la tarjeta. Por favor volvé a intentarlo.';
                            this.sharedService.showErrorAlert(null, errMsg);
                        }
                    );
                }
            });
    }

    findEmail() {

        if (this.email.invalid) return;

        this.apiCustomerService.findCustomerByEmail(this.email.value).then(
            (customer: string) => {

                this.notRegisteredEmail = false;

            }, (err) => {
                this.notRegisteredEmail = true;
            }
        );
    }

    //form validators
    private _getPaymentMethods() {

        // No hacer la llamada si no esta logueado
        if (!this.customer) return;

        this.apiAccountService.getAllCards(this.customer.id).then(
            (cards: CustomerCardModel[]) => {

                this.customer.cards = cards;

                this.currentMethod = this.customer.cards.find((x) => x.isCurrent);
                if (this.currentMethod) {
                    this.selectedMethod = this.currentMethod.token;
                } else if (cards?.length > 0) {
                    // Seleccionar la primer tarjeta como default
                    this.currentMethod = cards[0];
                }

                this.onSetCurrentMethod.emit(this.currentMethod);
                this.paymentMethods = this.customer.cards;
            }
        );
    }

    get email() { return this._formGet(this.guestForm, 'email'); }
    get invalidEmail(): boolean {
        if (this.email && this.email.value) {
            this.email.setValue(this.email.value.trim());
        }
        return this._isInvalid(this.guestForm, 'email', this.touched);
    }

    get idNumber() { return this._formGet(this.guestForm, 'idNumber'); }
    get invalidIdNumber(): boolean {
        return this._isInvalid(this.guestForm, 'idNumber', this.touched);
    }

    get name() { return this._formGet(this.guestForm, 'name'); }
    get invalidName(): boolean {
        return this._isInvalid(this.guestForm, 'name', this.touched);
    }

    get surname() { return this._formGet(this.guestForm, 'surname'); }
    get invalidSurname(): boolean {
        return this._isInvalid(this.guestForm, 'surname', this.touched);
    }

    private _formGuestInit() {

        this.guestForm = this.formBuilder.group(
            {
                email: new UntypedFormControl(
                    '',
                    [Validators.required, Validators.email]
                ),
                name: new UntypedFormControl(
                    '',
                    [Validators.required]
                ),
                surname: new UntypedFormControl(
                    '',
                    [Validators.required]
                ),
                idNumber: new UntypedFormControl(
                    '',
                    this.showIdNumber ? [Validators.required, Validators.minLength(8)] : []
                )
            }
        );

        this.guestForm.valueChanges.subscribe((value) => {
            if (this.guestForm.valid) this.onSetGuestPurchase.emit(this.guestForm);
        });
    }

}
